import type {
  AttendanceParams,
  CallViewParams,
  GeoUserParams,
  GetDashboardSalesParams,
  IdleParams,
  JointVisitParams,
  VisitFormParams,
} from "@/types/dashboardtypes";
import { privateRequest } from "../axios";

const ENDPOINTS = {
  DASHBOARD_STATS: "api/CRM/Stats",
  DASHBOARD_SALES: "api/CRM/SalesReport",
  DASHBOARD_VISIT: "api/CRM/LeadVisitReportByDate",
  DASHBOARD_CALLING: "api/CRM/Report/CallLogs",
  DASHBOARD_LEAD: "api/CRM/Report/LeadsAdded",
  DASHBOARD_OPPORTUNITY_TEAM: "api/CRM/Report/Opportunities/Team",
  DASHBOARD_EXPENSE: "api/Expense/ReportByDate",
  DASHBOARD_CATEGORY_EXPENSE: "api/Expense/ReportByCategory",
  DASHBOARD_OPPORTUNITY_VALUE: "api/CRM/Report/Opportunities/Status",
  DASHBOARD_OPPORTUNITY_COUNT: "api/CRM/Report/Opportunities/Status",
  DASHBOARD_LEAD_STAGE: "api/CRM/Report/LeadsByStage",
  DASHBOARD_WISHES: "api/Wishes/GetTodayWishes",
  DASHBOARD_SEND_WISHES: "api/Wishes/SendWishMail",
  DASHBOARD_GEO_USER: "api/GeoUserDetail",
  DASHBOARD_ALL_CUSTOMER: "api/GetAllCustomerViewReport",
  DASHBOARD_ALL_CUSTOMER_JOINTVISIT: "api/GetAllCustomerJointVisitReport",
  DASHBOARD_IDLE: "api/GetIdleDetails",
  DASHBOARD_TEAM_LEAVE: "api/GetTeamLeaveDetails",
  DASHBOARD_VISIT_FORM: "api/DynamicVisitForm",
  DASHBOARD_CALL_VIEW: "api/Call/View",
  DASHBOARD_DSR_PDF: "api/Users/DailyStatusReport",
  DASHBOARD_PENDING_REQUESTS_COUNT: "API/PendingRequestsCount",
};

// Generic GET helper
const getRequest = async <T>(url: string, params?: any): Promise<T> => {
  const response = await privateRequest.get(url, { params });
  return response.data;
};

// Generic POST helper
const postRequest = async <T>(url: string, body?: any): Promise<T> => {
  const response = await privateRequest.post(url, body);
  return response.data;
};

export const getDsrPdf = async (userId: number, date: string) => {
  const response = await privateRequest.get(`${ENDPOINTS.DASHBOARD_DSR_PDF}?id=${userId}&date=${date}`, {
    responseType: "blob",
  });
  return response.data;
}

// -------------------------
// Dashboard API methods
// -------------------------
const DashboardService = {
  getDashboardStats: () => getRequest<any>(ENDPOINTS.DASHBOARD_STATS),

  getDashboardSalesReport: (params: GetDashboardSalesParams) =>
    postRequest<any[]>(ENDPOINTS.DASHBOARD_SALES, params),
  getDashboardVisitReport: (params: GetDashboardSalesParams) =>
    postRequest<any[]>(ENDPOINTS.DASHBOARD_VISIT, params),
  getDashboardCallingReport: (params: GetDashboardSalesParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_CALLING, params),
  getDashboardLeadsReport: (params: GetDashboardSalesParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_LEAD, params),
  getDashboardOpportunityTeamReport: (params: GetDashboardSalesParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_OPPORTUNITY_TEAM, params),
  getDashboardExpenseReport: (params: GetDashboardSalesParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_EXPENSE, params),
  getDashboardExpenseCategoryReport: (params: GetDashboardSalesParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_CATEGORY_EXPENSE, params),
  getDashboardOpportunityValueReport: (params: GetDashboardSalesParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_OPPORTUNITY_VALUE, params),
  getDashboardOpportunityCountReport: (params: GetDashboardSalesParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_OPPORTUNITY_COUNT, params),
  getDashboardCustomerCountReport: (params: GetDashboardSalesParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_LEAD_STAGE, params),

  getTodaysWishes: () => getRequest<any>(ENDPOINTS.DASHBOARD_WISHES),
  sendWishes: (data: { wishType: string; userId: number }) =>
    getRequest<any>(ENDPOINTS.DASHBOARD_SEND_WISHES, {
      wishType: data.wishType,
      wishUserId: data.userId,
    }),

  GeoUserDetail: (body: GeoUserParams) =>
    postRequest<any[]>(ENDPOINTS.DASHBOARD_GEO_USER, body),
  GetAllCustomerViewReport: (params: AttendanceParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_ALL_CUSTOMER, params),
  GetAllCustomerJointVisitReport: (body: JointVisitParams) =>
    postRequest<any[]>(ENDPOINTS.DASHBOARD_ALL_CUSTOMER_JOINTVISIT, body),
  GetIdleDetails: (params: IdleParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_IDLE, params),
  GetTeamLeaveDetails: (params: IdleParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_TEAM_LEAVE, params),
  DynamicVisitForm: (body: VisitFormParams) =>
    postRequest<any[]>(ENDPOINTS.DASHBOARD_VISIT_FORM, body),
  GetCallView: (params: CallViewParams) =>
    getRequest<any[]>(ENDPOINTS.DASHBOARD_CALL_VIEW, params),
  getPendingRequestsCount: () =>
    getRequest<any>(ENDPOINTS.DASHBOARD_PENDING_REQUESTS_COUNT),
};

export default DashboardService;
